import {
  useGetListingsSimilarQuery,
  useRequestViewingMutation,
} from '@kijiji/generated/graphql-types'
import { type FieldProps, type FormikErrors, Field, Formik } from 'formik'
import { useRouter } from 'next/router'
import { useSession } from 'next-auth/react'
import { Trans, useTranslation } from 'next-i18next'
import { useState } from 'react'
import { useTheme } from 'styled-components'

import { LinkCustom } from '@/components/shared/link-custom/LinkCustom'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { refreshSession } from '@/features/auth/utils/refreshSession'
import { DatePickerField } from '@/features/listing/components/request-viewing/RequestViewForm/DatePickerField'
import {
  Form,
  PrePopulateNameSection,
  SubscriptionFormBodyContainer,
  SubscriptionFormFooter,
  SubscriptionFormSection,
} from '@/features/listing/components/request-viewing/styled'
import { ListingsSimilarModalBody } from '@/features/listing/components/similar/ListingsSimilarModalBody'
import { LISTING_SIMILAR_MODAL_ITEM_COUNT } from '@/features/listing/constants/listingSimilar'
import { isExternalListingId } from '@/features/listing/utils/isExternalListingId'
import { generateRequestViewingSubscriptionTrackingLabel } from '@/features/listing/utils/request-viewing/generateRequestViewingSubscriptionTrackingLabel'
import { PrePopulateNameCheckbox } from '@/features/profile/components/PrePopulateNameCheckbox'
import { useLocalUserPreferences } from '@/features/profile/hooks/useLocalUserPreferences'
import { useFormValidation } from '@/hooks/useFormValidation'
import { trackEvent } from '@/lib/ga'
import { type GaEvent, GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { BodyText } from '@/ui/atoms/body-text'
import { Button } from '@/ui/atoms/button'
import { Spacing } from '@/ui/atoms/spacing'
import { SystemMessage } from '@/ui/molecules/system-message'
import { TextField } from '@/ui/molecules/text-field/TextField'
import { TextAreaField } from '@/ui/molecules/textarea-field'
import { sendToLogger } from '@/utils/sendToLogger'
import { validationFnPerField } from '@/utils/validation'

export type RequestViewingSubscriptionFormProps = {
  listingId: string
  source: string
  onSuccessFallback: () => void
}

type FormValues = {
  fullName: string
  requestViewingDates: string[]
  message: string
}

export const RequestViewingSubscriptionForm = ({
  listingId,
  source,
  onSuccessFallback,
}: RequestViewingSubscriptionFormProps) => {
  const { query } = useRouter()
  const isExternalId = isExternalListingId(query)

  const { t } = useTranslation([
    TRANSLATION_KEYS.COMMON,
    TRANSLATION_KEYS.REQUEST_VIEWING,
    TRANSLATION_KEYS.R2S,
  ])
  const { colors, spacing } = useTheme()
  const { status, data: session } = useSession()
  const { validateForm } = useFormValidation()
  const { shouldPrePopulateName, savedName, handleUpdateSavedName } = useLocalUserPreferences()
  const [requestViewing, { loading: isSubmittingMessage, error: requestViewingError }] =
    useRequestViewingMutation()

  const [formSubmitted, setFormSubmitted] = useState(false)

  const { data } = useGetListingsSimilarQuery({
    fetchPolicy: 'cache-first',
    variables: { listingId, limit: LISTING_SIMILAR_MODAL_ITEM_COUNT, isExternalId },
    onError: (err) =>
      sendToLogger(err, {
        fingerprint: ['request-viewing-subscription-form'],
        extra: { listingId },
      }),
  })

  const email = session?.user?.email
  const initialValues: FormValues = {
    fullName: shouldPrePopulateName ? savedName : '',
    requestViewingDates: [],
    message: t('request_viewing:form.message.placeholder'),
  }

  const validationObj = {
    fullName: validationFnPerField.name,
    message: validationFnPerField.message,
  }

  const successMessage = {
    title: t('r2s:message_form.success.title.professional'),
    description: t('r2s:message_form.success.description.professional.real_estate', { email }),
  }

  const handleTracking = (values: FormValues, action: GaEvent) => {
    const messageChanged = values.message !== t('request_viewing:form.message.placeholder')
    const label = generateRequestViewingSubscriptionTrackingLabel({
      messageChanged,
      fullName: values.fullName,
      datesSelected: values.requestViewingDates.length,
      saveName: shouldPrePopulateName,
    })

    trackEvent({
      action,
      label,
      customParameters: {
        eventCategory: source,
      },
    })
  }

  const handleSubmitClick = (values: FormValues, errors?: FormikErrors<FormValues>) => {
    handleTracking(values, GA_EVENT.RequestViewingAttempt)

    if (errors && Object.keys(errors).length) {
      trackEvent({
        action: GA_EVENT.RequestViewingFail,
        label: `subscription_seller=1;message_error=${errors.message ? '1' : '0'};full_name_error=${errors.fullName ? '1' : '0'}`,
        customParameters: {
          eventCategory: source,
        },
      })
    }
  }

  const handleValidation = (values: FormValues) => {
    const errors: FormikErrors<FormValues> = validateForm(validationObj, values)
    return errors
  }

  const handleSubmit = async (values: FormValues) => {
    const hasSimilarListings = data?.listingsSimilar && data.listingsSimilar.length > 0

    const trimmedName = values.fullName.trim()
    handleUpdateSavedName(trimmedName)

    try {
      await refreshSession(status)

      await requestViewing({
        variables: {
          listingRequestViewingId: listingId,
          message: values.message,
          name: trimmedName,
          requestViewingDates: values.requestViewingDates,
        },
      })

      handleTracking(values, GA_EVENT.RequestViewingSuccess)

      trackEvent({
        action: GA_EVENT.R2SEmailSuccess,
        label: 'request_a_viewing=1',
        customParameters: {
          eventCategory: source,
        },
      })

      // if there are similar listings, show success message in modal
      // else, close the modal and show success message with the request viewing button
      if (hasSimilarListings) {
        setFormSubmitted(true)
      } else {
        onSuccessFallback()
      }
    } catch (error) {
      trackEvent({
        action: GA_EVENT.RequestViewingFail,
        label: `subscription_seller=1;`,
        customParameters: {
          eventCategory: source,
        },
      })
    }
  }

  return (
    <>
      {!formSubmitted ? (
        <Formik initialValues={initialValues} onSubmit={handleSubmit} validate={handleValidation}>
          {({ values, errors, touched }) => (
            <Form>
              <SubscriptionFormBodyContainer flexDirection="column">
                <SubscriptionFormSection flexDirection="column" flexGrow={1}>
                  {requestViewingError && (
                    <Spacing mTop={spacing.default} mBottom={spacing.default}>
                      <SystemMessage
                        variation="error"
                        title={t('common:system_messages.generic_error.title')}
                        description={t('request_viewing:notification.error.description')}
                        hideCloseButton
                      />
                    </Spacing>
                  )}
                  <Spacing mBottom={spacing.default}>
                    <BodyText color={colors.grey.primary} size="large" weight="medium">
                      {t('request_viewing:form.greeting')}
                    </BodyText>
                  </Spacing>
                  <Field name="message">
                    {({ field }: FieldProps) => (
                      <TextAreaField
                        bottom={spacing.default}
                        id="message"
                        label={t('common:forms.inputs.message.label')}
                        error={
                          errors.message && touched.message ? t(String(errors.message)) : undefined
                        }
                        {...field}
                      />
                    )}
                  </Field>
                  <Field name="requestViewingDates">
                    {({ field }: FieldProps) => <DatePickerField {...field} />}
                  </Field>

                  <Spacing mTop={spacing.default} mBottom={spacing.default}>
                    <BodyText color={colors.grey.primary} size="large" weight="medium">
                      {t('request_viewing:form.thank_you')}
                    </BodyText>
                  </Spacing>
                  <Field name="fullName">
                    {({ field }: FieldProps) => (
                      <TextField
                        bottom="0"
                        label={t('common:forms.inputs.name.label')}
                        id="fullName"
                        error={
                          errors.fullName && touched.fullName
                            ? t(String(errors.fullName))
                            : undefined
                        }
                        {...field}
                      />
                    )}
                  </Field>
                </SubscriptionFormSection>
                <PrePopulateNameSection flexDirection="column" flexGrow={1}>
                  <Spacing mBottom={spacing.defaultSmall}>
                    <PrePopulateNameCheckbox />
                  </Spacing>
                  <BodyText size="small" color={colors.grey.light1}>
                    {t('request_viewing:form.info_email', { email })}
                  </BodyText>
                </PrePopulateNameSection>
              </SubscriptionFormBodyContainer>
              <SubscriptionFormFooter>
                <Button
                  onClick={() => handleSubmitClick(values, errors)}
                  isLoading={isSubmittingMessage}
                  type="submit"
                  variant="primary"
                  isFullWidth
                >
                  {t('request_viewing:form.submit_request')}
                </Button>
                <Spacing mTop={spacing.default}>
                  <BodyText size="small" color={colors.grey.light1} textAlign="center">
                    <Trans
                      i18nKey="request_viewing:form.terms"
                      components={[
                        <LinkCustom
                          hasUnderline
                          href={t('routes:external.terms_of_use.href')}
                          key="termsLink"
                          noChildren
                          rel="noreferrer"
                          size="small"
                          target="_blank"
                          variant="tertiary"
                        />,
                        <LinkCustom
                          hasUnderline
                          href={t('routes:external.privacy_policy.href')}
                          key="privacyLink"
                          noChildren
                          rel="noreferrer"
                          size="small"
                          target="_blank"
                          variant="tertiary"
                        />,
                      ]}
                    />
                  </BodyText>
                </Spacing>
              </SubscriptionFormFooter>
            </Form>
          )}
        </Formik>
      ) : (
        <Spacing
          mTop={spacing.large}
          mBottom={spacing.large}
          mLeft={spacing.large}
          mRight={spacing.large}
        >
          <ListingsSimilarModalBody
            successMessage={successMessage}
            listings={data?.listingsSimilar ?? []}
            title={t('listing:listings_similar.title')}
          />
        </Spacing>
      )}
    </>
  )
}
